import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Breadcrumb,
  Card,
  Descriptions,
  Flex,
  message,
  QRCode,
  Typography,
} from 'antd';
import { HomeOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import { DoorButton } from './doorButton';
import SEO from './SEO';
import atact120 from '../assets/atact120.png';
import {
  colourPalette,
  notificationTimeout,
  notificationType,
} from '../common/constants';
import debugLog from '../common/debugLog';
import validateString from '../common/validator';
import { firGetSiteInformation, firGetSiteURL } from '../firebase';
import { getPortalAblyClient, getDoorList } from '../service/clientCache';
import { AblyProvider } from '../service/ablyContext';

export default function HomePage({ site }) {
  const [ablyClient, setAblyClient] = useState(null);
  const [activeTabKey, setActiveTabKey] = useState('qrCode');
  const [doorList, setDoorList] = useState([]);
  const [doorLoading, setDoorLoading] = useState(false);
  const [qrCodeColour, setQrCodeColour] = useState(colourPalette.cplDisabled);
  const [siteAccount, setSiteAccount] = useState(''); // account registration
  const [siteInfo, setSiteInfo] = useState('');
  const [siteVMS, setSiteVMS] = useState('');

  const [messageApi, contextHolder] = message.useMessage();

  // destructure imports
  const { Text } = Typography;

  const siteItems = [
    {
      key: 'address',
      label: 'Address',
      span: 'filled',
      children: siteInfo.address,
    },
    {
      key: 'siteType',
      label: 'Type',
      span: 'filled',
      children: siteInfo.siteType,
    },
  ];

  const contentList = {
    qrCode: (
      <div
        style={{
          boxSizing: 'border-box',
          display: 'grid',
          // never shrink below 160px (no overlap), but stretch equally (1fr)
          gridTemplateColumns: 'repeat(auto-fit, minmax(192px, 1fr))',
          gap: '72px',
          margin: '0px',
          // 3 * 192px + 2 * 72px (gap) + 2 * 16px (padding) = 752px
          maxWidth: '752px',
          padding: '16px',
          width: '100%',
        }}
      >
        <div
          style={{
            alignItems: 'center', // keep items centred in their column
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Flex align="center" gap="small" justify="center" vertical>
            {siteVMS && (
              <QRCode
                bordered
                color={qrCodeColour}
                errorLevel="Q"
                icon={atact120}
                iconSize={40}
                size={160}
                type="svg"
                value={siteVMS}
              />
            )}
            <Text type="secondary">Check In</Text>
          </Flex>
        </div>
        <div
          style={{
            alignItems: 'center', // keep items centred in their column
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Flex align="center" gap="small" justify="center" vertical>
            {siteAccount && (
              <QRCode
                bordered
                color={qrCodeColour}
                errorLevel="Q"
                icon={atact120}
                iconSize={40}
                size={160}
                type="svg"
                value={siteAccount}
              />
            )}
            <Text type="secondary">Account Registration</Text>
          </Flex>
        </div>
      </div>
    ),
    siteInfo: (
      <Descriptions
        bordered={false}
        colon
        items={siteItems}
        layout="horizontal"
      />
    ),
    doorControl: doorLoading ? (
      <>Loading doors...</>
    ) : (
      <AblyProvider client={ablyClient}>
        <div
          style={{
            boxSizing: 'border-box',
            display: 'grid',
            // never shrink below 160px (no overlap), but stretch equally (1fr)
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: '16px',
            margin: '0px',
            // 4 * 160px + 3 * 16px (gap) + 2 * 16px (padding) = 720px
            maxWidth: '720px',
            padding: '16px',
            width: '100%',
          }}
        >
          {doorList.map((door) => (
            <div
              key={door.id}
              style={{
                alignItems: 'center', // keep items centred in their column
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <DoorButton
                {...door} // passes any future keys down automatically
                doorId={door.id}
                siteId={site.id}
              />
            </div>
          ))}
        </div>
      </AblyProvider>
    ),
  };

  const handleTabChange = (key) => {
    setActiveTabKey(key);
  };

  const openMessage = useCallback(
    (content, duration, type) => {
      messageApi.open({ content, duration, type });
    },
    [messageApi],
  );

  const tabList = [
    { key: 'qrCode', tab: 'QR Code' },
    { key: 'siteInfo', tab: 'Site Info' },
    { key: 'doorControl', tab: 'Door Control' },
  ];

  useEffect(() => {
    if (validateString(site.id) === false) return undefined;

    const clientPromise = getPortalAblyClient(site.id);
    clientPromise.then((resolvedClient) => {
      setAblyClient(resolvedClient); // resolve promise inside state hook
    });

    getDoorList(site.id)
      .then((doorMap) => {
        const listDoor = Object.entries(doorMap).map(
          ([id, config]) => ({ id, ...config }), // flatten map into array
        );
        setDoorList(listDoor);
      })
      .catch((error) => {
        console.error('-- Failed to load doors:', error);
      })
      .finally(() => {
        setDoorLoading(false);
      });

    // explicitly disconnect when navigating away or unmounting
    return () => {
      clientPromise.then((resolvedClient) => {
        if (resolvedClient && typeof resolvedClient.close === 'function') {
          // clear the connection cleanly on unmount using promise chain
          debugLog('-- Unmounting: closing Ably WebSocket connection...');
          resolvedClient.close(); // tell Ably to terminate the connection
        }
      });
    };
  }, [site.id]);

  useEffect(() => {
    // destructure imports
    const { cplError, cplNormal } = colourPalette;
    const { timeoutError } = notificationTimeout;
    const { typeError } = notificationType;

    if (validateString(site.id) === false) {
      console.warn('-- Site ID invalid');
      return;
    }

    firGetSiteInformation(site.id)
      .then((infoSite) => {
        setSiteInfo(infoSite);
      })
      .catch((error) => {
        console.warn(error);
        openMessage(error, timeoutError, typeError);
      });

    firGetSiteURL(site.id)
      .then((siteURL) => {
        setQrCodeColour(cplNormal);
        setSiteAccount(siteURL.account);
        setSiteVMS(siteURL.vms);
        debugLog(`-- Account Registration: ${siteURL.account}`);
        debugLog(`-- Check In: ${siteURL.vms}`);
      })
      .catch((error) => {
        setQrCodeColour(cplError);
        console.warn(error);
        openMessage(error, timeoutError, typeError);
      });
  }, [openMessage, site.id, siteAccount, siteVMS]);

  return (
    <>
      <SEO description="Home Page" name="qiwei" title="Home" type="webapp" />
      <div>
        <Breadcrumb
          items={[
            {
              title: (
                <Link to="..">
                  <HomeOutlined className="dropdown-icon-margin" />
                  <span>Home</span>
                </Link>
              ),
            },
          ]}
        />
      </div>
      {contextHolder}
      <Card
        activeTabKey={activeTabKey}
        className="setting card"
        hoverable
        onTabChange={handleTabChange}
        tabList={tabList}
      >
        {contentList[activeTabKey]}
      </Card>
    </>
  );
}

HomePage.propTypes = {
  site: PropTypes.shape({ id: PropTypes.string.isRequired }).isRequired,
};
